import { z } from 'zod';
import { INTERVENTION_STATUSES, type InterventionStatus } from './states/intervention';

const uuid = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, 'UUID invalide');
const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date AAAA-MM-JJ');
const time = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Heure HH:MM');

/**
 * Planning et interventions (CDC §8, §9). Une intervention appartient à un dossier « À planifier »
 * (contrôle manuel fait) ; elle est proposée à UN technicien (« À accepter »), qui accepte ou refuse
 * avec motif ; le jour J il prend en charge sa tournée ; statuts terrain jusqu'à « Terminée » ou
 * « Impossible » (motif). Le statut du dossier suit automatiquement (acteur système).
 */
export interface InterventionRecord {
  id: string;
  version: number;
  updatedAt: string;
  dossierId: string;
  territoryId: string;
  status: InterventionStatus;
  technicianId: string | null;
  technicianName: string | null;
  /** techniciens en renfort : ils voient l'intervention ; le technicien principal la fait avancer */
  coTechnicianIds: string[];
  coTechnicianNames: string[];
  /** le poseur a contacté le client et fixé lui-même le rendez-vous */
  clientContactedAt: string | null;
  clientContactedByName: string | null;
  /** date prévue (AAAA-MM-JJ) et créneau facultatif */
  scheduledDate: string | null;
  slotStart: string | null;
  slotEnd: string | null;
  /** ressource nacelle réservée (repérage par commune / semaine) */
  needsNacelle: boolean;
  /** commune d'intervention (adresse d'intervention, sinon facturation) */
  city: string | null;
  postalCode: string | null;
  /** vente / pose / entretien / sav (signature visuelle du type de dossier) */
  activity: string | null;
  notes: string | null;
  statusReasonId: string | null;
  statusComment: string | null;
  proposedAt: string | null;
  acceptedAt: string | null;
  takenChargeAt: string | null;
  onSiteAt: string | null;
  completedAt: string | null;
  /** technicien ayant effectivement réalisé l'intervention (visible du commercial) */
  completedById: string | null;
  completedByName: string | null;
  /** intervention de SAV (§12) */
  savId: string | null;
  createdAt: string;
}

export const interventionCreateInput = z.object({
  id: uuid,
  dossierId: uuid,
  scheduledDate: date.nullish().transform((v) => v ?? null),
  slotStart: time.nullish().transform((v) => v ?? null),
  slotEnd: time.nullish().transform((v) => v ?? null),
  /** par défaut : nacelle si le dossier l'indique */
  needsNacelle: z.boolean().nullish(),
  notes: z.string().trim().max(2000).nullish().transform((v) => v || null),
  /** intervention de SAV (§12) sur une installation terminée */
  savId: uuid.nullish().transform((v) => v ?? null),
});
export type InterventionCreateInput = z.input<typeof interventionCreateInput>;

export const interventionFields = z.object({
  scheduledDate: date.nullable(),
  slotStart: time.nullable(),
  slotEnd: time.nullable(),
  needsNacelle: z.boolean(),
  notes: z.string().trim().max(2000).nullable(),
});
export const INTERVENTION_MUTABLE_FIELDS = ['scheduledDate', 'slotStart', 'slotEnd', 'needsNacelle', 'notes'] as const;

export const interventionTransitionInput = z.object({
  from: z.enum(INTERVENTION_STATUSES),
  to: z.enum(INTERVENTION_STATUSES),
  reasonId: uuid.nullish().transform((v) => v ?? null),
  comment: z.string().trim().max(2000).nullish().transform((v) => v || null),
  /** proposition : technicien et, si connus, date / créneau */
  technicianId: uuid.nullish().transform((v) => v ?? null),
  scheduledDate: date.nullish(),
  slotStart: time.nullish(),
  slotEnd: time.nullish(),
});
export type InterventionTransitionInput = z.input<typeof interventionTransitionInput>;

/**
 * Rendez-vous fixé par le poseur (intervention à accepter, acceptée ou planifiée) : il a contacté le client et indique
 * la date et l'heure. L'intervention devient « Planifiée ».
 */
export const interventionAppointmentInput = z
  .object({
    scheduledDate: date,
    slotStart: time,
    slotEnd: time.nullish().transform((v) => v ?? null),
    clientContacted: z.literal(true, { message: 'Confirmez avoir contacté le client' }),
    comment: z.string().trim().max(2000).nullish().transform((v) => v || null),
  })
  .refine((v) => !v.slotEnd || v.slotEnd > v.slotStart, { path: ['slotEnd'], message: 'Fin après le début' });
export type InterventionAppointmentInput = z.input<typeof interventionAppointmentInput>;
/** statuts où le poseur peut fixer (ou déplacer) lui-même le rendez-vous */
export const APPOINTMENT_STATUSES: InterventionStatus[] = ['proposed', 'accepted', 'scheduled'];

/** Interventions « ouvertes » (ni terminées, ni annulées) */
export const OPEN_INTERVENTION: InterventionStatus[] = ['to_assign', 'proposed', 'accepted', 'scheduled', 'taken_charge', 'on_site', 'impossible'];
/** Prévues mais pas encore clôturées : en retard si la date est passée */
export const LATE_CANDIDATE: InterventionStatus[] = ['accepted', 'scheduled', 'taken_charge', 'on_site'];
/** Intervention en retard : date passée sans compte rendu (« Terminée » ou « Impossible ») */
export const isLateIntervention = (i: Pick<InterventionRecord, 'status' | 'scheduledDate'>, today: string) =>
  LATE_CANDIDATE.includes(i.status) && !!i.scheduledDate && i.scheduledDate < today;
/** En cours sur le terrain */
export const FIELD_INTERVENTION: InterventionStatus[] = ['taken_charge', 'on_site'];

/* ------------------------------------------------------------------ vues calendrier */

export type PlanningView = 'day' | 'week' | 'month';
export const PLANNING_VIEW_LABELS: Record<PlanningView, string> = { day: 'Aujourd’hui', week: '7 jours', month: 'Mois' };

const pad = (n: number) => String(n).padStart(2, '0');
export const isoDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export function addDays(day: string, n: number): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return isoDate(new Date(y, m - 1, d + n));
}
/** Période couverte par une vue (bornes incluses) : jour, 7 jours à partir du jour, mois civil. */
export function planningRange(view: PlanningView, day: string): { from: string; to: string; days: string[] } {
  let from = day;
  let to = day;
  if (view === 'week') to = addDays(day, 6);
  if (view === 'month') {
    const [y, m] = day.split('-').map(Number) as [number, number];
    from = `${y}-${pad(m)}-01`;
    to = isoDate(new Date(y, m, 0));
  }
  const days: string[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) days.push(d);
  return { from, to, days };
}

/** Semaine ISO (lundi) d'une date — repérage « nacelle prévue dans la commune cette semaine ». */
export function weekStart(day: string): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  const dt = new Date(y, m - 1, d);
  const dow = (dt.getDay() + 6) % 7;
  return isoDate(new Date(y, m - 1, d - dow));
}

/** Vue anonymisée pour les commerciaux : volumes par commune et par jour, présence d'une nacelle. */
export interface PlanningSummaryRow {
  date: string;
  city: string;
  count: number;
  nacelle: boolean;
}

/**
 * Intervention pour un partenaire (sous-traitance de pose), saisie en une fois depuis le planning : le partenaire
 * (existant, ou nouveau nom créé à la volée), son client, la prestation (puissance → rémunération du poseur), le
 * rendez-vous et le technicien. Le serveur crée le client du partenaire, le chantier (sans vente), l'envoie « À
 * planifier », puis crée l'intervention (proposée au technicien s'il est choisi).
 */
export const partnerJobInput = z
  .object({
    clientId: uuid,
    /** client déjà enregistré (choisi dans la base) : ses coordonnées ne sont pas ressaisies */
    existingClient: z.boolean().default(false),
    dossierId: uuid,
    interventionId: uuid,
    partnerId: uuid.nullish().transform((v) => v ?? null),
    /** nouveau partenaire (s'il n'existe pas encore sous ce nom) */
    partnerName: z.string().trim().max(100).nullish().transform((v) => v || null),
    territoryId: uuid,
    client: z.object({
      kind: z.enum(['individual', 'company']).default('individual'),
      lastName: z.string().trim().max(100).nullish(),
      firstName: z.string().trim().max(100).nullish(),
      companyName: z.string().trim().max(200).nullish(),
      /** professionnel : personne à contacter, SIRET (facultatif) */
      contactName: z.string().trim().max(200).nullish(),
      siret: z.string().trim().nullish().transform((v) => (v ? v.replace(/\s/g, '') : null)),
      phone: z.string().trim().max(30).nullish(),
      addressLine1: z.string().trim().max(200).nullish(),
      postalCode: z.string().trim().max(10).nullish(),
      city: z.string().trim().max(100).nullish(),
    }).default({ kind: 'individual', siret: null }),
    dossierTypeId: uuid,
    /** n° ou référence du bon du partenaire (facultatif), repris dans l'intitulé du chantier */
    partnerReference: z.string().trim().max(100).nullish().transform((v) => v || null),
    installAtHeight: z.boolean(),
    installAccess: z.enum(['none', 'ladder', 'nacelle']).nullish().transform((v) => v ?? null),
    installHeightM: z.number().min(0).max(100).nullish().transform((v) => v ?? null),
    lines: z.array(z.object({ itemId: uuid, quantity: z.number().positive().max(1000) })).min(1, 'Choisissez au moins une prestation'),
    scheduledDate: date.nullish().transform((v) => v ?? null),
    slotStart: time.nullish().transform((v) => v ?? null),
    slotEnd: time.nullish().transform((v) => v ?? null),
    technicianId: uuid.nullish().transform((v) => v ?? null),
    /** techniciens en renfort dès la création (intervention à plusieurs) */
    coTechnicianIds: z.array(uuid).max(5).default([]),
    notes: z.string().trim().max(2000).nullish().transform((v) => v || null),
  })
  .refine((v) => v.partnerId || (v.partnerName && v.partnerName.length >= 2), {
    message: 'Choisissez le partenaire ou saisissez son nom',
    path: ['partnerName'],
  });
export type PartnerJobInput = z.input<typeof partnerJobInput>;

/** équipe d'une intervention : techniciens en renfort (le technicien principal est choisi à la proposition) */
export const interventionCrewInput = z.object({
  coTechnicianIds: z.array(uuid).max(5, '5 techniciens en renfort au plus'),
});

/** le technicien fait partie de l'intervention (principal ou en renfort) */
export const isOnIntervention = (i: { technicianId: string | null; coTechnicianIds?: string[] }, userId: string) =>
  i.technicianId === userId || (i.coTechnicianIds ?? []).includes(userId);
